import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import APP_ASSETS from "@/assets";
import { SIDEBAR_ITEMS } from "@/constants";
import { Link, useNavigate } from "react-router-dom";
import { cn, copyToClipboard, handleShare, maskAddress } from "@/lib/utils";
import { Skeleton } from "./ui/skeleton";
import { useMerchant, useSmartWallet, useUSDT } from "@/contexts";
import { useMerchantWrite } from "@/hooks";
import { useToast } from "./ui/use-toast";
import { useState } from "react";
import { LogOut } from "lucide-react";
import { Button } from "./ui/button/button";
import { ButtonSize, ButtonType } from "./ui/button/button.type";
import { isOrderExpired } from "@/utils/orders";
import PrivacyPolicy from "./PrivacyPolicy";
import { useSelector } from "react-redux";
import { StateStatusEnum } from "@/enums/state-status.enum";
import { trackAmplitudeEvent } from "@/utils/amplitude";

interface SidebarItemProps {
  name: string;
  logo: string;
  routeURL: string;
  openDetailsModal: () => void;
}

const SidebarItem = ({
  name,
  logo,
  routeURL,
  openDetailsModal,
}: SidebarItemProps) => {
  const navigate = useNavigate();
  return (
    <div
      onClick={() => {
        if (routeURL == "wallet") {
          trackAmplitudeEvent("WALLET_DETAILS_CLICKED");
          openDetailsModal();
          return;
        }
        trackAmplitudeEvent(`SIDEBAR_NAVIGATION_TO_${name.toUpperCase()}`);
        navigate(routeURL);
      }}
      className="flex w-full items-center justify-start gap-2 py-2 hover:cursor-pointer">
      <img className="h-7 w-7" src={logo} alt={logo} />
      <p className="text-lg font-medium text-text-light">{name}</p>
    </div>
  );
};

const SheetContentContainer = () => {
  const [isToggling, setIsToggling] = useState(false);

  const { toast } = useToast();
  const { smartWalletAddress, openDetailsModal } = useSmartWallet();

  const {
    usdtBalance,
    isLoading: balanceLoading,
    hasFetched: balanceFetched,
  } = useUSDT();
  const {
    merchantDetails,
    isLoading: merchantLoading,
    hasFetched: merchantFetched,
    refreshMerchant,
  } = useMerchant();

  const { toggleOnlineOffline } = useMerchantWrite();

  const handleToggleOnlineOffline = async () => {
    const newStatus = merchantDetails?.isOnline ? "Offline" : "Online";
    trackAmplitudeEvent("MERCHANT_STATUS_TOGGLE", { newStatus });

    setIsToggling(true);
    toast({
      title: "Initiating status toggle...",
      description: `Toggling status to ${newStatus}`,
    });
    const result = await toggleOnlineOffline();
    //@ts-ignore

    if (result?.afterWait?.status === "success") {
      trackAmplitudeEvent("MERCHANT_STATUS_TOGGLE_SUCCESS", { newStatus });
      toast({
        title: "Status toggled",
        description: "Status indicator has been toggled.",
      });
    } else {
      trackAmplitudeEvent("MERCHANT_STATUS_TOGGLE_FAILED", { newStatus });
      toast({
        title: "Status wasn't toggled",
        description: "Status couldn't be toggled.",
        variant: "destructive",
      });
    }
    setIsToggling(false);
    void refreshMerchant();
  };

  const [showPrivacyPolicy, setShowPrivacyPolicy] = useState(false);

  const handlePrivacyPolicyClick = () => {
    trackAmplitudeEvent("PRIVACY_POLICY_CLICKED");
    setShowPrivacyPolicy(true);
  };

  return (
    <SheetContent
      className="no-scrollbar w-[70%] overflow-y-scroll p-0"
      side="left">
      <SheetHeader className="h-40 bg-sidebar-trasactions-top-bg p-4">
        <SheetTitle>
          <div className="flex items-center justify-between">
            <div className="text-xl font-black uppercase text-black">MENU</div>
            <SheetClose>
              <img
                className="h-[27px] w-[20px] object-contain"
                src={APP_ASSETS.ICONS.Close_Btn_Black}
                alt="p2p.me"
              />
            </SheetClose>
          </div>
        </SheetTitle>
        <div className="my-4 flex flex-col gap-1 rounded-lg border border-primary bg-bg-tertiary px-6 py-4">
          <div className="flex flex-col items-start justify-center gap-2">
            <div className="text-left text-xs font-medium tracking-wider text-text-primary">
              Your P2P.me wallet address (Base)
            </div>
            <div className="flex items-center gap-2">
              <p className="text-sm text-text-light">
                {maskAddress(smartWalletAddress ?? "", 5)}
              </p>
              <img
                onClick={() => {
                  trackAmplitudeEvent("WALLET_ADDRESS_COPIED");
                  copyToClipboard(smartWalletAddress as string);
                }}
                className="cursor-pointer"
                src={APP_ASSETS.ICONS.PASTE}
                alt={APP_ASSETS.ICONS.PASTE}
              />
            </div>
          </div>
          <div className="border-[1px] border-stroke-grey"></div>
          <div className="flex flex-col gap-2">
            <div className="flex flex-col items-start justify-center gap-1">
              <div className="text-xs font-medium tracking-wider text-text-primary">
                Staked USDC balance
              </div>
              <div className="value-contianer font-satoshiBold flex items-center gap-2 py-1 text-xl text-text-light">
                <img
                  className="mb-1 h-5 w-5"
                  src={APP_ASSETS.TOKENS.USDC_BLUE}
                  alt="tether_icon"
                />
                <div className="flex items-start gap-2">
                  {!merchantLoading && merchantFetched && merchantDetails ? (
                    <p className="h-6 font-medium text-text-light">
                      {merchantDetails.stake ?? 0}
                    </p>
                  ) : (
                    <Skeleton className="h-6 w-8" />
                  )}
                  <span className="text-[10px] tracking-wider">USDC</span>
                </div>
              </div>
            </div>
          </div>
          <div className="border-[1px] border-stroke-grey"></div>
          <div className="flex flex-col gap-2">
            <div className="flex flex-col items-start justify-center gap-1">
              <div className="text-xs tracking-wider text-text-primary">
                Available USDC to stake
              </div>
              <div className="value-contianer font-satoshiBold flex items-center gap-2 py-1 text-xl text-text-light">
                <img
                  className="mb-1 h-5 w-5"
                  src={APP_ASSETS.TOKENS.USDC_BLUE}
                  alt="tether_icon"
                />
                <div className="flex items-start gap-2">
                  {!balanceLoading && balanceFetched ? (
                    <p className="h-6 font-medium tracking-wider text-text-light">
                      {usdtBalance}
                    </p>
                  ) : (
                    <Skeleton className="h-6 w-12" />
                  )}{" "}
                  <span className="text-[10px] tracking-wider">USDC</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </SheetHeader>
      {/* ONLINE STATUS */}
      <section
        onClick={handleToggleOnlineOffline}
        className="mx-10 mt-36 flex h-8 cursor-pointer items-center justify-between rounded-full border-[1px] border-stroke-grey bg-bg-tertiary">
        {isToggling || !merchantFetched || merchantLoading ? (
          <Skeleton className="h-full w-full rounded-full" />
        ) : (
          <>
            <div
              className={cn(
                "flex h-full w-1/2 items-center justify-center rounded-full bg-bg-tertiary",
                { "bg-bg-primary": merchantDetails?.isOnline },
              )}>
              <p
                className={cn("font-medium tracking-widest", {
                  "text-text-light": !merchantDetails?.isOnline,
                })}>
                Online
              </p>
            </div>
            <div
              className={cn(
                "flex h-full w-1/2 items-center justify-center rounded-full bg-bg-tertiary",
                { "bg-bg-primary": !merchantDetails?.isOnline },
              )}>
              <p
                className={cn("font-medium tracking-widest", {
                  "text-text-light": merchantDetails?.isOnline,
                })}>
                Offline
              </p>
            </div>
          </>
        )}
      </section>
      {/* ONLINE STATUS */}
      {/* SIDEBAR ITEMS */}
      <section className="flex flex-col items-center justify-center p-4">
        {SIDEBAR_ITEMS.map((item) => (
          <SidebarItem
            key={item.name}
            name={item.name}
            logo={item.logo}
            routeURL={item.routeURL}
            openDetailsModal={openDetailsModal}
          />
        ))}
      </section>
      {/* SIDEBAR ITEMS */}
      {/* FOOTER */}
      <SheetFooter>
        <div className="absolute bottom-0 flex w-full flex-col gap-2 px-6 py-4">
          <div className="font-bold uppercase text-text-light">Find us on</div>
          <div className="bottom-container flex w-full items-center justify-between">
            <div className="icons flex gap-1">
              <Link
                target="_blank"
                className="cursor-pointer"
                onClick={() =>
                  trackAmplitudeEvent("SOCIAL_LINK_CLICKED", {
                    platform: "twitter",
                  })
                }
                to="https://twitter.com/P2PX_finance">
                <img src={APP_ASSETS.ICONS.TWITTER} alt="Twitter" />
              </Link>
              <Link
                target="_blank"
                className="cursor-pointer"
                onClick={() =>
                  trackAmplitudeEvent("SOCIAL_LINK_CLICKED", {
                    platform: "telegram",
                  })
                }
                to="https://t.me/p2px_finance">
                <img src={APP_ASSETS.ICONS.TELEGRAM} alt="Telegram" />
              </Link>
            </div>
            <Button
              label={"Share"}
              onClick={() => {
                trackAmplitudeEvent("SHARE_APP_CLICKED");
                handleShare();
              }}
              size={ButtonSize.small}
            />
          </div>
          <div className="pb-4 text-center">
            <span
              onClick={handlePrivacyPolicyClick}
              className="font-satoshiRegular cursor-pointer text-sm text-text-light/60 hover:text-text-light">
              Privacy Policy and Terms
            </span>
            <p className="font-satoshiRegular cursor-pointer text-sm text-text-light/30 hover:text-text-light">
              Version: {import.meta.env.APP_VERSION}
            </p>
          </div>
          {showPrivacyPolicy && (
            <PrivacyPolicy
              onClose={() => setShowPrivacyPolicy(false)}
              showAccept={false}
            />
          )}
        </div>
      </SheetFooter>
      {/* FOOTER */}
    </SheetContent>
  );
};

export default function Topbar() {
  const navigate = useNavigate();
  const ordersData = useSelector((state: any) => state.orders.data);
  const ordersLoading = useSelector(
    (state: any) => state.orders.status == StateStatusEnum.LOADING,
  );
  const ordersFetched = useSelector(
    (state: any) => state.orders.status == StateStatusEnum.SUCCESS,
  );

  const { logout } = useSmartWallet();
  const _orders = ordersData?.latestOrders
    ?.toJS()
    .filter((order: any) => isOrderExpired(parseInt(order?.placedTimestamp)));
  return (
    <nav className="absolute top-0 flex h-14 w-full justify-start border-b-4 border-primary bg-bg-tertiary px-4 py-[12px]">
      <Sheet>
        <SheetTrigger>
          <img
            className="h-8 w-8"
            src={APP_ASSETS.ICONS.HAMBURGER}
            alt="hamburger-icon"
          />
        </SheetTrigger>
        <SheetContentContainer />
      </Sheet>
      <img
        className="ml-4 flex h-[27px] self-center"
        src={APP_ASSETS.ICONS.P2PX_LOGO}
        alt="p2p.me"
      />
      {/* REMOVE THIS SECTION */}
      <div className="flex w-full items-center justify-end gap-1">
        {/* <OrderNotificationDrawer type={OrderNotificationType.SEND} />
        <OrderNotificationDrawer type={OrderNotificationType.RECEIVE} /> */}
        {!ordersLoading && ordersFetched ? (
          <Button
            label={`${_orders.length} New Order${
              _orders.length === 1 ? "" : "s"
            }`}
            onClick={() => {
              trackAmplitudeEvent("NEW_ORDERS_CLICKED", {
                orderCount: _orders.length,
              });
              navigate("/new-orders");
            }}
            type={ButtonType.glass}
          />
        ) : (
          <Skeleton className="h-10 w-32 rounded-md" />
        )}
        <LogOut
          onClick={() => {
            trackAmplitudeEvent("LOGOUT_CLICKED_TOPBAR");
            logout();
          }}
          className="mx-2 h-[27px] w-[20px] text-text-primary hover:cursor-pointer"
        />
      </div>
      {/* REMOVE THIS SECTION */}
    </nav>
  );
}
